/**
 * Live-trading SVG overlay: the invisible full-width grab strips at each
 * draggable trading line (position/order SL/TP/entry). Extracted from
 * Chart.tsx. Dragging a strip starts a trading drag (which on mouseup
 * modifies the real order); this renderer just lays out the hit-areas, so
 * it takes the live refs + trading state + the drag/highlight starters via
 * context. Body verbatim; closed-over references became ctx.*.
 */
import type { MouseEvent, MutableRefObject, RefObject, ReactNode } from "react";
import type { IChartApi } from "lightweight-charts";
import type { TradingPosition, TradingOrder } from "@/api/types";
import type { AnySeries } from "./chartData";
import type { DragTarget } from "./types";

export interface TradingStripsCtx {
  seriesRef: MutableRefObject<AnySeries | null>;
  chartRef: MutableRefObject<IChartApi | null>;
  containerRef: RefObject<HTMLDivElement>;
  activeDrawingTool: string | null;
  canTrade: boolean;
  tradingConnected: boolean;
  showTradingTrades: boolean;
  showTradingSlTp: boolean;
  tradingPositions: TradingPosition[];
  tradingOrders: TradingOrder[];
  symbol: string;
  startTradingDrag: (
    target: Extract<DragTarget, { type: "trading" }>,
  ) => (e: MouseEvent) => void;
  setHighlightGroup: (
    g: { scope: "pos" | "order"; ticket: number } | null,
  ) => void;
  /** Bare left-click on a live trade's line: opens a draft for whichever
   *  of SL / TP the trade is missing. No-op when it has both. */
  onTradeClick: (scope: "pos" | "order", ticket: number) => void;
}

export function renderTradingDragStrips(ctx: TradingStripsCtx): ReactNode {
  const series = ctx.seriesRef.current;
  const chart = ctx.chartRef.current;
  const container = ctx.containerRef.current;
  if (!series || !chart || !container) return null;
  if (ctx.activeDrawingTool) return null;
  if (!ctx.canTrade || !ctx.tradingConnected || !ctx.showTradingTrades)
    return null;
  const psW = chart.priceScale("right").width();
  const stripW = Math.max(0, container.clientWidth - psW);
  const H = 9;
  // `target` null = highlight-only strip (e.g. an open position's
  // filled entry, which can't be moved). Hovering any strip highlights
  // the whole group; only strips with a target start a drag.
  const strip = (
    key: string,
    price: number,
    scope: "pos" | "order",
    ticket: number,
    target: Extract<DragTarget, { type: "trading" }> | null,
  ) => {
    const yc = series.priceToCoordinate(price);
    if (yc == null) return null;
    return (
      <rect
        key={key}
        data-trading-strip="1"
        x={0}
        y={yc - H / 2}
        width={stripW}
        height={H}
        fill="transparent"
        style={{
          cursor: target ? "ns-resize" : "pointer",
          pointerEvents: "all",
        }}
        // Click highlights the whole group (and, if draggable, begins a
        // drag). startTradingDrag also sets the highlight; the
        // highlight-only entry just selects.
        onMouseDown={
          target
            ? ctx.startTradingDrag(target)
            : (e: MouseEvent) => {
                if (e.button !== 0) return;
                e.stopPropagation();
                e.preventDefault();
                ctx.setHighlightGroup({ scope, ticket });
                // A filled entry can't be dragged, so a click on it is
                // free to mean "manage this trade".
                ctx.onTradeClick(scope, ticket);
              }
        }
      />
    );
  };
  const out: ReactNode[] = [];
  for (const p of ctx.tradingPositions) {
    if (p.symbol !== ctx.symbol) continue;
    // Filled entry — highlight-only (not draggable).
    out.push(
      strip(`d-pos-entry-${p.ticket}`, p.price_open, "pos", p.ticket, null),
    );
    if (ctx.showTradingSlTp && p.sl > 0)
      out.push(
        strip(`d-pos-sl-${p.ticket}`, p.sl, "pos", p.ticket, {
          type: "trading",
          ticket: p.ticket,
          kind: "pos-sl",
          initial: { entry: p.price_open, sl: p.sl, tp: p.tp },
          preview: { price: p.sl, sl: 0, tp: 0 },
          anchorPrice: p.sl,
          anchorPnl: p.sl_pnl ?? null,
        }),
      );
    if (ctx.showTradingSlTp && p.tp > 0)
      out.push(
        strip(`d-pos-tp-${p.ticket}`, p.tp, "pos", p.ticket, {
          type: "trading",
          ticket: p.ticket,
          kind: "pos-tp",
          initial: { entry: p.price_open, sl: p.sl, tp: p.tp },
          preview: { price: p.tp, sl: 0, tp: 0 },
          anchorPrice: p.tp,
          anchorPnl: p.tp_pnl ?? null,
        }),
      );
  }
  for (const o of ctx.tradingOrders) {
    if (o.symbol !== ctx.symbol) continue;
    out.push(
      strip(`d-ord-entry-${o.ticket}`, o.price_open, "order", o.ticket, {
        type: "trading",
        ticket: o.ticket,
        kind: "order-entry",
        initial: { entry: o.price_open, sl: o.sl, tp: o.tp },
        preview: { price: o.price_open, sl: o.sl, tp: o.tp },
        anchorPrice: o.price_open,
        anchorPnl: null,
      }),
    );
    if (ctx.showTradingSlTp && o.sl > 0)
      out.push(
        strip(`d-ord-sl-${o.ticket}`, o.sl, "order", o.ticket, {
          type: "trading",
          ticket: o.ticket,
          kind: "order-sl",
          initial: { entry: o.price_open, sl: o.sl, tp: o.tp },
          preview: { price: o.sl, sl: 0, tp: 0 },
          anchorPrice: o.sl,
          anchorPnl: o.sl_pnl ?? null,
        }),
      );
    if (ctx.showTradingSlTp && o.tp > 0)
      out.push(
        strip(`d-ord-tp-${o.ticket}`, o.tp, "order", o.ticket, {
          type: "trading",
          ticket: o.ticket,
          kind: "order-tp",
          initial: { entry: o.price_open, sl: o.sl, tp: o.tp },
          preview: { price: o.tp, sl: 0, tp: 0 },
          anchorPrice: o.tp,
          anchorPnl: o.tp_pnl ?? null,
        }),
      );
  }
  return out;
}
